import React from "react";
import { Alert, Modal, Space, Table, Tag, Typography } from "antd";
import { useTranslation } from "react-i18next";
import { weightLabel } from "../../../../functions/articleCode";
import { itemWeight } from "../../../../functions/pickRoute";
import { SHOP_COLORS } from "../product-data/constants";

const { Text } = Typography;

const RESULT_COLORS = { deleted: "red", emptied: "orange", partial: "blue" };

/**
 * What "Opruimen" is about to do (functions/pickRoute planOrphanCleanup): per spot the products
 * that no longer exist and what the spot is afterwards. Shown before anything is written, because
 * the warning's whole point is that a product can be gone from the webshop while its box is not.
 */
const OrphanCleanupModal = ({ open, plan, notLoaded, busy, onCancel, onConfirm }) => {
  const { t } = useTranslation();
  const rows = [
    ...plan.deletes.map(({ entry, removed }) => ({ entry, removed, result: "deleted", kept: 0 })),
    ...plan.updates.map(({ entry, products, removed }) => ({ entry, removed, result: products.length > 0 ? "partial" : "emptied", kept: products.length })),
  ].sort((a, b) => (a.entry.sequence || 0) - (b.entry.sequence || 0));
  const count = (result) => rows.filter((row) => row.result === result).length;

  const columns = [
    {
      title: t("pick_route.cleanup_col_entry"),
      dataIndex: "entry",
      render: (entry) => entry.label || <Text type="secondary">-</Text>,
    },
    {
      title: t("pick_route.cleanup_col_location"),
      dataIndex: "location",
      width: 70,
      render: (_, row) => (row.entry.location ? <Tag>{row.entry.location}</Tag> : <Text type="secondary">-</Text>),
    },
    {
      title: t("pick_route.cleanup_col_removed"),
      dataIndex: "removed",
      render: (removed) => (
        <Space direction="vertical" size={0}>
          {removed.map((item) => (
            <span key={`${item.shopSource}|${item.productId}|${itemWeight(item) || ""}`}>
              <Tag color={SHOP_COLORS[item.shopSource]}>{item.shopSource}</Tag>
              {item.name || item.productId}
              {itemWeight(item) ? ` · ${weightLabel(itemWeight(item))}` : ""}
            </span>
          ))}
          {removed.length === 0 && <Text type="secondary">{t("pick_route.cleanup_nothing_on_it")}</Text>}
        </Space>
      ),
    },
    {
      title: t("pick_route.cleanup_col_result"),
      dataIndex: "result",
      width: 140,
      render: (result, row) => <Tag color={RESULT_COLORS[result]}>{t(`pick_route.cleanup_result_${result}`, { count: row.kept })}</Tag>,
    },
  ];

  return (
    <Modal
      open={open}
      width={860}
      title={t("pick_route.cleanup_title")}
      okText={t("pick_route.cleanup_confirm", { count: rows.length })}
      okButtonProps={{ danger: true, disabled: rows.length === 0, loading: busy }}
      cancelText={t("cancel")}
      onCancel={onCancel}
      onOk={onConfirm}
    >
      <p>{t("pick_route.cleanup_intro")}</p>
      {notLoaded.length > 0 && (
        <Alert type="warning" showIcon className="mb-3" message={t("pick_route.cleanup_not_loaded", { shops: notLoaded.join(", ") })} />
      )}
      <Space wrap className="mb-3">
        {count("deleted") > 0 && <Tag color={RESULT_COLORS.deleted}>{t("pick_route.cleanup_deleted", { count: count("deleted") })}</Tag>}
        {count("emptied") > 0 && <Tag color={RESULT_COLORS.emptied}>{t("pick_route.cleanup_emptied", { count: count("emptied") })}</Tag>}
        {count("partial") > 0 && <Tag color={RESULT_COLORS.partial}>{t("pick_route.cleanup_partial", { count: count("partial") })}</Tag>}
      </Space>
      <Table
        size="small"
        rowKey={(row) => row.entry.id}
        columns={columns}
        dataSource={rows}
        pagination={rows.length > 50 ? { pageSize: 50 } : false}
        scroll={{ y: 420 }}
      />
    </Modal>
  );
};

export default OrphanCleanupModal;
